/**
 * DATA LAYER (DL): PERSONAL QUICK REACTION (REACCIÓN RÁPIDA PERSONAL - HU-04)
 * Módulo de comunicación HTTP nativo (Fetch API) con la API REST de Interaktia-V2 para la dinámica individual de aficionados.
 * Gestiona el ciclo de vida completo de participación móvil (registro, emisión de One-Time Token OTT de 5 min,
 * envío de jugada con tiempo de reacción en milisegundos, telemetría dual) y endpoints de administración y moderación.
 * 
 * @module utilities/DL/PersonalQuickReaction
 */

/**
 * Resuelve y retorna la URL base para los endpoints públicos del controlador de PersonalQuickReaction.
 * 
 * @returns {string} URL base formateada para el controlador público.
 */
export const getApiBaseUrl = () => {
    const rawApiUrl = process.env.REACT_APP_API_URL || 'https://localhost:50586/api/';
    const normalizedUrl = rawApiUrl.endsWith('/') ? rawApiUrl.slice(0, -1) : rawApiUrl;
    return `${normalizedUrl}/v1/personal-quick-reaction`;
};

/**
 * Resuelve y retorna la URL base para los endpoints administrativos del controlador de PersonalQuickReaction.
 * 
 * @returns {string} URL base formateada para el controlador administrativo.
 */
export const getAdminApiBaseUrl = () => {
    return `${getApiBaseUrl()}/admin`;
};

/**
 * Sanea y valida identificadores alfanuméricos y valores de cabecera HTTP para prevenir inyecciones CRLF y manipulación de rutas.
 * 
 * @param {string|null} val - Valor a sanear.
 * @returns {string} Valor limpio sin saltos de línea ni caracteres de control.
 */
const sanitizeHeaderValue = (val) => {
    if (!val || typeof val !== 'string') return '';
    return val.replace(/[\r\n\t\0]/g, '').trim().substring(0, 100);
};

/**
 * Obtiene las cabeceras estándar de autenticación Bearer y JSON para peticiones administrativas.
 * 
 * @param {boolean} [isJson=true] - Si se debe incluir Content-Type: application/json.
 * @returns {Object} Cabeceras con token JWT si está disponible.
 */
const getAuthHeaders = (isJson = true) => {
    const headers = {};
    if (isJson) {
        headers['Content-Type'] = 'application/json';
    }

    if (typeof localStorage !== 'undefined') {
        const token = localStorage.getItem('interaktia_token') || localStorage.getItem('token') || '';
        if (token) {
            headers['Authorization'] = `Bearer ${sanitizeHeaderValue(token)}`;
        }
    }

    return headers;
};

/**
 * Construye las cabeceras HTTP estándar para peticiones públicas, adjuntando X-Room-Id si está presente.
 * 
 * @param {string|null} [roomId=null] - Identificador único de la sala.
 * @param {Object} [customHeaders={}] - Cabeceras personalizadas adicionales.
 * @returns {Object} Objeto de cabeceras listo para Fetch API.
 */
const buildHeaders = (roomId = null, customHeaders = {}) => {
    const headers = {
        'Content-Type': 'application/json',
        ...customHeaders
    };

    if (roomId) {
        const cleanRoomId = sanitizeHeaderValue(String(roomId));
        if (cleanRoomId) {
            headers['X-Room-Id'] = cleanRoomId;
        }
    }

    return headers;
};

// =========================================================================
// ENDPOINTS PÚBLICOS DE JUEGO Y PARTICIPACIÓN MÓVIL
// =========================================================================

/**
 * Obtiene la configuración pública activa y paquete de sprites del tema para la sala.
 * GET /api/v1/personal-quick-reaction/config/{roomId?}
 * 
 * @param {string|null} [roomId=null] - Identificador GUID de la sala (opcional, fallback a sala activa).
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Configuración pública.
 */
export const GetPublicConfig = async (roomId = null) => {
    try {
        const sanitizedRoomId = roomId ? encodeURIComponent(String(roomId).trim()) : '';
        const requestUrl = sanitizedRoomId
            ? `${getApiBaseUrl()}/config/${sanitizedRoomId}`
            : `${getApiBaseUrl()}/config`;

        const httpResponse = await fetch(requestUrl, {
            method: 'GET',
            headers: buildHeaders(roomId)
        });

        if (!httpResponse.ok) {
            const errorPayload = await httpResponse.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || `Error HTTP ${httpResponse.status} al consultar la configuración pública.`
            };
        }

        const responseData = await httpResponse.json();
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData
        };
    } catch (networkError) {
        return {
            success: false,
            message: 'Error de red o conexión al consultar la configuración de Reacción Rápida Personal.'
        };
    }
};

/**
 * Registra a un nuevo participante para la dinámica o recupera un registro activo emitiendo un One-Time Token (OTT) de 5 minutos.
 * POST /api/v1/personal-quick-reaction/register
 * 
 * @param {Object} payload - Datos de registro (roomId, participantName, email, phone, acceptedTerms, acceptedPrivacy, etc.).
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Resultado del registro con OTT.
 */
export const RegisterParticipant = async (payload) => {
    try {
        if (!payload || !payload.roomId) {
            return { success: false, message: 'Datos de registro incompletos o roomId no especificado.' };
        }

        const requestUrl = `${getApiBaseUrl()}/register`;

        const httpResponse = await fetch(requestUrl, {
            method: 'POST',
            headers: buildHeaders(payload.roomId),
            body: JSON.stringify(payload)
        });

        const responseData = await httpResponse.json().catch(() => ({}));

        if (!httpResponse.ok) {
            return {
                success: false,
                message: responseData.message || `Error HTTP ${httpResponse.status} al registrar al participante.`,
                data: responseData
            };
        }

        return {
            success: responseData.success !== false,
            data: responseData.data !== undefined ? responseData.data : responseData,
            message: responseData.message || 'Participante registrado exitosamente.'
        };
    } catch (networkError) {
        return {
            success: false,
            message: 'Error de red al registrar al participante en Reacción Rápida Personal.'
        };
    }
};

/**
 * Procesa la jugada enviada por el participante con su token OTT y tiempo de reacción registrado en milisegundos.
 * POST /api/v1/personal-quick-reaction/submit-play
 * 
 * @param {Object} payload - Objeto con participantId, oneTimeToken, reactionTimeMs, isEarlyFault, penaltyTimeMs, earlyAttemptsCount.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Resultado oficial de la jugada y ranking.
 */
export const SubmitPlay = async (payload) => {
    try {
        if (!payload || !payload.participantId || !payload.oneTimeToken) {
            return { success: false, message: 'Parámetros de jugada incompletos (participantId y oneTimeToken son requeridos).' };
        }

        const requestUrl = `${getApiBaseUrl()}/submit-play`;

        const httpResponse = await fetch(requestUrl, {
            method: 'POST',
            headers: buildHeaders(payload.roomId || null),
            body: JSON.stringify(payload)
        });

        const responseData = await httpResponse.json().catch(() => ({}));

        if (!httpResponse.ok) {
            return {
                success: false,
                message: responseData.message || `Error HTTP ${httpResponse.status} al enviar la jugada.`,
                data: responseData
            };
        }

        return {
            success: responseData.success !== false,
            data: responseData.data !== undefined ? responseData.data : responseData,
            message: responseData.message || 'Jugada procesada exitosamente.'
        };
    } catch (networkError) {
        return {
            success: false,
            message: 'Error de red al enviar la jugada de Reacción Rápida Personal.'
        };
    }
};

/**
 * Registra eventos de telemetría y pasos del embudo de conversión para la dinámica en el backend.
 * POST /api/v1/personal-quick-reaction/telemetry
 * 
 * @param {Object} payload - Datos de telemetría (roomId, sessionId, stepName, source, clientIpAddress).
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Confirmación de recepción.
 */
export const TrackTelemetry = async (payload) => {
    try {
        if (!payload || !payload.roomId || !payload.stepName) {
            return { success: false, message: 'Parámetros de telemetría incompletos.' };
        }

        const requestUrl = `${getApiBaseUrl()}/telemetry`;

        const httpResponse = await fetch(requestUrl, {
            method: 'POST',
            headers: buildHeaders(payload.roomId),
            body: JSON.stringify(payload)
        });

        const responseData = await httpResponse.json().catch(() => ({ success: true }));

        return {
            success: httpResponse.ok,
            data: responseData
        };
    } catch (networkError) {
        return {
            success: false,
            message: 'Error de red al enviar telemetría.'
        };
    }
};

/**
 * Obtiene la tabla de clasificación (ranking) oficial de la sala (Top N participantes).
 * GET /api/v1/personal-quick-reaction/ranking/{roomId}?top={top}
 * 
 * @param {string} roomId - Identificador GUID de la sala.
 * @param {number} [top=20] - Cantidad máxima de registros en el ranking.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Colección de participantes en ranking.
 */
export const GetRanking = async (roomId = null, top = 20) => {
    try {
        const sanitizedRoomId = roomId ? encodeURIComponent(String(roomId).trim()) : '';
        const sanitizedTop = encodeURIComponent(String(top || 20));
        const requestUrl = sanitizedRoomId
            ? `${getApiBaseUrl()}/ranking/${sanitizedRoomId}?top=${sanitizedTop}`
            : `${getApiBaseUrl()}/ranking?top=${sanitizedTop}`;

        const httpResponse = await fetch(requestUrl, {
            method: 'GET',
            headers: buildHeaders(roomId)
        });

        if (!httpResponse.ok) {
            const errorPayload = await httpResponse.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || `Error HTTP ${httpResponse.status} al consultar el ranking.`
            };
        }

        const responseData = await httpResponse.json();
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData
        };
    } catch (networkError) {
        return {
            success: false,
            message: 'Error de red al consultar el ranking de Reacción Rápida Personal.'
        };
    }
};

/**
 * Obtiene el participante líder/ganador actual en la tabla de clasificación de la sala.
 * GET /api/v1/personal-quick-reaction/winner/{roomId?}
 * 
 * @param {string|null} [roomId=null] - Identificador GUID de la sala (opcional, fallback a sala activa).
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Participante ganador.
 */
export const GetWinner = async (roomId = null) => {
    try {
        const sanitizedRoomId = roomId ? encodeURIComponent(String(roomId).trim()) : '';
        const requestUrl = sanitizedRoomId
            ? `${getApiBaseUrl()}/winner/${sanitizedRoomId}`
            : `${getApiBaseUrl()}/winner`;

        const httpResponse = await fetch(requestUrl, {
            method: 'GET',
            headers: buildHeaders(roomId)
        });

        if (!httpResponse.ok) {
            const errorPayload = await httpResponse.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || `Error HTTP ${httpResponse.status} al consultar el ganador.`
            };
        }

        const responseData = await httpResponse.json();
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData
        };
    } catch (networkError) {
        return {
            success: false,
            message: 'Error de red al consultar el ganador actual.'
        };
    }
};

// =========================================================================
// ENDPOINTS ADMINISTRATIVOS Y DE MODERACIÓN
// =========================================================================

/**
 * Obtiene el conjunto consolidado de datos administrativos (configuración, temas, participantes, ranking y métricas).
 * GET /api/v1/personal-quick-reaction/admin/data/{roomId?}
 * 
 * @param {string|null} [roomId=null] - Identificador GUID de la sala (opcional, fallback a sala activa).
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Datos consolidados de administración.
 */
export const GetAdminData = async (roomId = null) => {
    try {
        const sanitizedRoomId = roomId ? encodeURIComponent(String(roomId).trim()) : '';
        const requestUrl = sanitizedRoomId
            ? `${getAdminApiBaseUrl()}/data/${sanitizedRoomId}`
            : `${getAdminApiBaseUrl()}/data`;

        const httpResponse = await fetch(requestUrl, {
            method: 'GET',
            headers: getAuthHeaders(true),
            credentials: 'include'
        });

        if (!httpResponse.ok) {
            const errorPayload = await httpResponse.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || `Error HTTP ${httpResponse.status} al obtener datos administrativos.`
            };
        }

        const responseData = await httpResponse.json();
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData
        };
    } catch (networkError) {
        return {
            success: false,
            message: 'Error de red al consultar datos administrativos de Reacción Rápida Personal.'
        };
    }
};

/**
 * Guarda y actualiza la configuración técnica y visual de la dinámica para la sala desde el panel de control.
 * POST /api/v1/personal-quick-reaction/admin/config
 * 
 * @param {Object} payload - Objeto con la configuración técnica y de temas.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Configuración persistida.
 */
export const UpdateAdminConfig = async (payload) => {
    try {
        if (!payload || !payload.roomId) {
            return { success: false, message: 'Datos de configuración incompletos (roomId es obligatorio).' };
        }

        const requestUrl = `${getAdminApiBaseUrl()}/config`;

        const httpResponse = await fetch(requestUrl, {
            method: 'POST',
            headers: getAuthHeaders(true),
            credentials: 'include',
            body: JSON.stringify(payload)
        });

        const responseData = await httpResponse.json().catch(() => ({}));

        if (!httpResponse.ok) {
            return {
                success: false,
                message: responseData.message || `Error HTTP ${httpResponse.status} al actualizar la configuración.`
            };
        }

        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData,
            message: responseData.message || 'Configuración actualizada exitosamente.'
        };
    } catch (networkError) {
        return {
            success: false,
            message: 'Error de red al actualizar la configuración administrativa.'
        };
    }
};

/**
 * Actualiza el estado de visibilidad, modo y códigos QR del overlay en pantalla.
 * POST /api/v1/personal-quick-reaction/admin/overlay
 * 
 * @param {Object} payload - Parámetros del overlay (roomId, overlayVisible, overlayMode, overlayQrCount, etc.).
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Configuración del overlay actualizada.
 */
export const UpdateAdminOverlay = async (payload) => {
    try {
        if (!payload || !payload.roomId) {
            return { success: false, message: 'Identificador de sala (RoomId) obligatorio.' };
        }

        const requestUrl = `${getAdminApiBaseUrl()}/overlay`;

        const httpResponse = await fetch(requestUrl, {
            method: 'POST',
            headers: getAuthHeaders(true),
            credentials: 'include',
            body: JSON.stringify(payload)
        });

        const responseData = await httpResponse.json().catch(() => ({}));

        if (!httpResponse.ok) {
            return {
                success: false,
                message: responseData.message || `Error HTTP ${httpResponse.status} al actualizar el overlay.`
            };
        }

        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData,
            message: responseData.message || 'Overlay actualizado exitosamente.'
        };
    } catch (networkError) {
        return {
            success: false,
            message: 'Error de red al actualizar el overlay.'
        };
    }
};

/**
 * Sube, valida firmas binarias y almacena un asset gráfico (fondo, logo, sprites, banners) para Reacción Rápida Personal.
 * POST /api/v1/personal-quick-reaction/admin/upload-asset
 * 
 * @param {File|Blob|FormData} fileOrFormData - Archivo o instancia FormData a subir.
 * @param {string} [assetType='sprite'] - Tipo de asset ('background' | 'logo' | 'sprite_back' | 'sprite_front' | etc.).
 * @param {string|null} [roomId=null] - Identificador GUID de la sala.
 * @returns {Promise<{success: boolean, data?: Object, url?: string, message?: string}>} URL y metadata del asset.
 */
export const UploadAsset = async (fileOrFormData, assetType = 'sprite', roomId = null) => {
    try {
        let requestFormData = fileOrFormData;
        if (!(fileOrFormData instanceof FormData)) {
            requestFormData = new FormData();
            requestFormData.append('file', fileOrFormData);
            if (assetType) requestFormData.append('assetType', assetType);
            if (roomId) requestFormData.append('roomId', roomId);
        }

        const headers = getAuthHeaders(false); // No establecer Content-Type para permitir multipart boundary
        const requestUrl = `${getAdminApiBaseUrl()}/upload-asset`;

        const httpResponse = await fetch(requestUrl, {
            method: 'POST',
            headers: headers,
            credentials: 'include',
            body: requestFormData
        });

        const responseData = await httpResponse.json().catch(() => ({}));

        if (!httpResponse.ok) {
            return {
                success: false,
                message: responseData.message || `Error HTTP ${httpResponse.status} al subir el asset gráfico.`
            };
        }

        return {
            success: responseData.success !== false,
            data: responseData,
            url: responseData.url || (responseData.data && responseData.data.url) || '',
            message: responseData.message || 'Asset subido y procesado exitosamente.'
        };
    } catch (networkError) {
        return {
            success: false,
            message: 'Error de red al subir el archivo gráfico a Reacción Rápida Personal.'
        };
    }
};

/**
 * Obtiene el catálogo de temas visuales disponibles para la sala.
 * GET /api/v1/personal-quick-reaction/admin/themes/{roomId}
 * 
 * @param {string} roomId - Identificador GUID de la sala.
 * @returns {Promise<{success: boolean, data?: Array<Object>, message?: string}>} Colección de temas.
 */
export const GetThemes = async (roomId) => {
    try {
        if (!roomId) {
            return { success: false, message: 'El identificador de sala (RoomId) es obligatorio.' };
        }

        const sanitizedRoomId = encodeURIComponent(String(roomId).trim());
        const requestUrl = `${getAdminApiBaseUrl()}/themes/${sanitizedRoomId}`;

        const httpResponse = await fetch(requestUrl, {
            method: 'GET',
            headers: getAuthHeaders(true),
            credentials: 'include'
        });

        if (!httpResponse.ok) {
            const errorPayload = await httpResponse.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || `Error HTTP ${httpResponse.status} al consultar temas.`
            };
        }

        const responseData = await httpResponse.json();
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData
        };
    } catch (networkError) {
        return {
            success: false,
            message: 'Error de red al consultar los temas de Reacción Rápida Personal.'
        };
    }
};

/**
 * Crea o actualiza un tema visual (sprites, fondos, banners) para la sala.
 * POST /api/v1/personal-quick-reaction/admin/themes
 * 
 * @param {Object} payload - Objeto con datos del tema y URLs de sprites.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Tema guardado.
 */
export const SaveTheme = async (payload) => {
    try {
        if (!payload || !payload.roomId) {
            return { success: false, message: 'Datos del tema incompletos (roomId es obligatorio).' };
        }

        const requestUrl = `${getAdminApiBaseUrl()}/themes`;

        const httpResponse = await fetch(requestUrl, {
            method: 'POST',
            headers: getAuthHeaders(true),
            credentials: 'include',
            body: JSON.stringify(payload)
        });

        const responseData = await httpResponse.json().catch(() => ({}));

        if (!httpResponse.ok) {
            return {
                success: false,
                message: responseData.message || `Error HTTP ${httpResponse.status} al guardar el tema.`
            };
        }

        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData,
            message: responseData.message || 'Tema guardado exitosamente.'
        };
    } catch (networkError) {
        return {
            success: false,
            message: 'Error de red al guardar el tema visual.'
        };
    }
};

/**
 * Otorga otra oportunidad (soft-reset) a un participante archivando su intento previo y registrando auditoría.
 * POST /api/v1/personal-quick-reaction/admin/reset-user
 * 
 * @param {Object} payload - Objeto con roomId, participantId, reason y operatorUser.
 * @returns {Promise<{success: boolean, message?: string}>} Confirmación del reinicio del participante.
 */
export const ResetParticipant = async (payload) => {
    try {
        if (!payload || !payload.roomId || !payload.participantId) {
            return { success: false, message: 'Identificadores de sala y participante requeridos.' };
        }

        const requestUrl = `${getAdminApiBaseUrl()}/reset-user`;

        const httpResponse = await fetch(requestUrl, {
            method: 'POST',
            headers: getAuthHeaders(true),
            credentials: 'include',
            body: JSON.stringify(payload)
        });

        const responseData = await httpResponse.json().catch(() => ({}));

        if (!httpResponse.ok) {
            return {
                success: false,
                message: responseData.message || `Error HTTP ${httpResponse.status} al reactivar participante.`
            };
        }

        return {
            success: true,
            message: responseData.message || 'Participante reactivado exitosamente.'
        };
    } catch (networkError) {
        return {
            success: false,
            message: 'Error de red al reactivar al participante.'
        };
    }
};

/**
 * Reinicia por completo la dinámica en la sala (eliminando participantes y telemetría), protegido con clave maestra.
 * POST /api/v1/personal-quick-reaction/admin/full-reset
 * 
 * @param {Object} payload - Objeto con roomId, masterPassword y operatorUser.
 * @returns {Promise<{success: boolean, message?: string}>} Confirmación del borrado total.
 */
export const FullReset = async (payload) => {
    try {
        if (!payload || !payload.roomId || !payload.masterPassword) {
            return { success: false, message: 'La contraseña maestra y el roomId son obligatorios.' };
        }

        const requestUrl = `${getAdminApiBaseUrl()}/full-reset`;

        const httpResponse = await fetch(requestUrl, {
            method: 'POST',
            headers: getAuthHeaders(true),
            credentials: 'include',
            body: JSON.stringify(payload)
        });

        const responseData = await httpResponse.json().catch(() => ({}));

        if (!httpResponse.ok) {
            return {
                success: false,
                message: responseData.message || `Error HTTP ${httpResponse.status} al reiniciar la sala.`
            };
        }

        return {
            success: true,
            message: responseData.message || 'Dinámica reiniciada completamente.'
        };
    } catch (networkError) {
        return {
            success: false,
            message: 'Error de red al ejecutar el reinicio maestro de la dinámica.'
        };
    }
};

// Aliases semánticos para conveniencia
export const ResetUser = ResetParticipant;

export default {
    getApiBaseUrl,
    getAdminApiBaseUrl,
    GetPublicConfig,
    RegisterParticipant,
    SubmitPlay,
    TrackTelemetry,
    GetRanking,
    GetWinner,
    GetAdminData,
    UpdateAdminConfig,
    UpdateAdminOverlay,
    UploadAsset,
    GetThemes,
    SaveTheme,
    ResetParticipant,
    ResetUser,
    FullReset
};
